import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../../hooks/useApi";
import AppContainer from "../../components/AppContainer";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Stat,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBoolean,
  useDisclosure,
} from "@chakra-ui/react";
import { ENDPOINT } from "../../config/endpoint.config";
import {
  IApiResponse,
  ILeaveResponse,
  IMLeaveResponse,
  IStateResponse,
} from "../../helper/Interface";
import moment from "moment";
import { FiEdit } from "react-icons/fi";
import AppHeader from "../../components/AppHeader";
import { AiFillDelete } from "react-icons/ai";
import { useToasts } from "react-toast-notifications";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSION } from "../../config/permission.config";
import AppSelect from "../../components/AppSelect";
import AppLoader from "../../components/AppLoader";
import AppNoData from "../../components/AppNoData";
import { formatDate, sortByFunc } from "../../helper/Utils";
import {
  BsCheckCircle,
  BsCheckCircleFill,
  BsListCheck,
  BsSortAlphaDown,
  BsSortAlphaDownAlt,
} from "react-icons/bs";
import AppTableHeadingWithSort from "../../components/AppTableHeadingWithSort";
import AppTabs from "../../components/AppTabs";
import { useAppSelector } from "../../app/store/store";

export const VIEWS = [
  {
    name: "Leave Policy",
    value: "leaves",
    index: 0,
  },
  {
    name: "M-Leave Policy",
    value: "m-leaves",
    index: 1,
  },
];

function MangeLeaves() {
  const [view, setView] = useState(VIEWS[0].value);
  const { contractTypes } = useAppSelector((state) => state.auth);
  const { get, put, post, Delete } = useApi();
  const { addToast } = useToasts();
  const { checkForPermission } = usePermission();
  const [isLoading, { on: onLoading, off: offLoading }] = useBoolean(true);
  const [leaves, setLeaves] = useState<ILeaveResponse[]>([]);
  const [mLeaves, setMLeaves] = useState<IMLeaveResponse[]>([]);

  const [leaveId, setLeaveId] = useState("");
  const [mLeaveId, setMLeaveId] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [numLeaves, setNumLeaves] = useState("");
  const [stateId, setStateId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [states, setStates] = useState<IStateResponse[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("state");
  const [sortMethodAsc, setSortMethodAsc] = useState<boolean>(true);
  const [contractTypeIds, setContractTypeIds] = useState<number[]>([]);
  const [err, setErr] = useState("");
  const {
    isOpen: isLeaveModalOpen,
    onClose: onLeaveModalClose,
    onOpen: onLeaveModalOpen,
  } = useDisclosure();
  const {
    isOpen: isMLeaveModalOpen,
    onClose: onMLeaveModalClose,
    onOpen: onMLeaveModalOpen,
  } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onClose: onDeleteModalClose,
    onOpen: onDeleteModalOpen,
  } = useDisclosure();
  useEffect(() => {
    getYears();
    getStates();
  }, []);

  useEffect(() => {
    if (view === VIEWS[0].value) {
      getAllLeaves();
    } else {
      getAllMLeaves();
    }
  }, [view]);

  const getYears = () => {
    const date = new Date();
    const years: string[] = [];
    for (let index = 1; index <= 10; index++) {
      years.push((date.getFullYear() + index).toString());
    }
    setYears(years);
  };
  const getStates = async () => {
    const res = await get<IStateResponse[]>(ENDPOINT["/master"]["/state"]);
    if (res?.length) {
      setStates(res);
    }
  };

  const getAllLeaves = async () => {
    setLeaves([]);
    onLoading();
    const res = await get<ILeaveResponse[]>(ENDPOINT["/master"]["/leave"]);
    offLoading();
    if (res?.length) {
      setLeaves(res);
    } else {
      setLeaves([]);
    }
  };
  const getAllMLeaves = async () => {
    setMLeaves([]);
    onLoading();
    const res = await get<IMLeaveResponse[]>(
      ENDPOINT["/master"]["/menstrual-leave-config"],
    );
    offLoading();
    if (res?.length) {
      setMLeaves(res);
    } else {
      setMLeaves([]);
    }
  };
  const statesWithMLeaves = useMemo<IMLeaveResponse[]>(() => {
    if (isLoading) return [];
    return states.map(({ id: stateId, name: state }) => {
      const mLeave = mLeaves.find((obj) => obj.stateId === stateId);
      return {
        id: mLeave?.id || 0,
        enabled: mLeave?.enabled || false,
        contractTypeIds: mLeave?.contractTypeIds || [],
        stateId,
      };
    });
  }, [states, mLeaves]);
  const isFutureDate = (effectiveDate: Date) => {
    effectiveDate.setHours(0, 0, 0, 0);
    let today = new Date();
    let todayMorningDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    if (effectiveDate.getTime() >= todayMorningDate.getTime()) {
      return true;
    }
    return false;
  };
  const onCreateLeave = () => {
    setLeaveId("");
    setNumLeaves("");
    setStateId("");
    setEffectiveDate("");
    onLeaveModalOpen();
  };
  const onEditLeave = (
    id: number,
    numLeaves: number,
    effectiveDate: string,
    stateId: string,
  ) => {
    setLeaveId(id.toString());
    setNumLeaves(numLeaves.toString());
    setStateId(stateId);
    setEffectiveDate(effectiveDate);
    onLeaveModalOpen();
  };
  const onDeleteLeaveConfirmation = (
    id: number,
    numLeaves: number,
    effectiveDate: string,
    stateId: string,
  ) => {
    setLeaveId(id.toString());
    setNumLeaves(numLeaves.toString());
    setStateId(stateId);
    setEffectiveDate(effectiveDate);
    onDeleteModalOpen();
  };
  const onSaveLeaves = () => {
    onLeaveModalClose();
    if (leaveId) {
      put<IApiResponse>(ENDPOINT["/master"]["/leave"] + `/${leaveId}`, {
        data: {
          numLeaves,
          stateId,
          effectiveDate,
        },
      }).then((res) => {
        addToast(res.message, {
          appearance: res.success ? "success" : "error",
        });
        if (res.success) {
          getAllLeaves();
        }
      });
    } else {
      post<IApiResponse>(ENDPOINT["/master"]["/leave"], {
        data: {
          numLeaves,
          stateId,
          effectiveDate,
        },
      }).then((res) => {
        addToast(res.message, {
          appearance: res.success ? "success" : "error",
        });
        if (res.success) {
          getAllLeaves();
        }
      });
    }
  };
  const onDeleteLeaves = () => {
    onDeleteModalClose();
    put<IApiResponse>(ENDPOINT["/master"]["/leave"] + `/${leaveId}`, {
      data: {
        numLeaves,
        stateId,
        effectiveDate,
        delete: true,
      },
    }).then((res) => {
      addToast(res.message, {
        appearance: res.success ? "success" : "error",
      });
      if (res.success) {
        getAllLeaves();
      }
    });
  };
  const onEditMLeave = (
    id: number,
    enabled: boolean,
    stateId: number,
    contractTypeIds: number[],
  ) => {
    setErr("");
    setMLeaveId(id);
    setEnabled(enabled);
    setStateId(stateId.toString());
    setContractTypeIds(contractTypeIds);
    onMLeaveModalOpen();
  };
  const onSaveMLeaves = () => {
    setErr("");
    if (!enabled && !mLeaveId) {
      setErr("No change detected!");
      return;
    }
    onMLeaveModalClose();
    if (mLeaveId) {
      (enabled
        ? put<IApiResponse>(
            ENDPOINT["/master"]["/menstrual-leave-config"] + `/${mLeaveId}`,
            {
              data: {
                enabled,
                contractTypeIds: enabled ? contractTypeIds : [],
              },
            },
          )
        : Delete<IApiResponse>(
            ENDPOINT["/master"]["/menstrual-leave-config"] + `/${mLeaveId}`,
          )
      ).then((res) => {
        addToast(res.message, {
          appearance: res.success ? "success" : "error",
        });
        if (res.success) {
          getAllMLeaves();
        }
      });
    } else {
      post<IApiResponse>(ENDPOINT["/master"]["/menstrual-leave-config"], {
        data: {
          enabled,
          contractTypeIds: enabled ? contractTypeIds : [],
          stateId,
        },
      }).then((res) => {
        addToast(res.message, {
          appearance: res.success ? "success" : "error",
        });
        if (res.success) {
          getAllMLeaves();
        }
      });
    }
  };

  return (
    <AppContainer
      heading="Leaves"
      info="Define maximum number of leaves to be assigned in a state along effective date."
    >
      <AppTabs value={view} setValue={(value) => setView(value)} tabs={VIEWS}>
        {checkForPermission(PERMISSION.Config["Leaves"].Update) &&
          view === VIEWS[0].value && (
            <Button ml={"4"} onClick={onCreateLeave}>
              + Add Leaves
            </Button>
          )}
      </AppTabs>

      <Flex overflow={"auto"}>
        {(view === VIEWS[0].value ? leaves : statesWithMLeaves).length ? (
          <>
            {view === VIEWS[0].value ? (
              <TableContainer
                background="white"
                width={"full"}
                border={"1px solid #F2F2F2"}
                borderRadius={"md"}
              >
                <Table variant="simple">
                  <Thead height={"48px"}>
                    <Tr>
                      <Th background="#EBF3F8" color="#616161">
                        Sr. No.
                      </Th>
                      <Th background="#EBF3F8" color="#616161">
                        <AppTableHeadingWithSort
                          value="state"
                          label="State"
                          sortBy={sortBy}
                          setSortBy={setSortBy}
                          sortMethodAsc={sortMethodAsc}
                          setSortMethodAsc={setSortMethodAsc}
                        />
                      </Th>
                      <Th background="#EBF3F8" color="#616161">
                        <AppTableHeadingWithSort
                          value="numLeaves"
                          label="Leaves"
                          sortBy={sortBy}
                          setSortBy={setSortBy}
                          sortMethodAsc={sortMethodAsc}
                          setSortMethodAsc={setSortMethodAsc}
                        />
                      </Th>
                      <Th background="#EBF3F8" color="#616161">
                        Effective Date
                      </Th>
                      <Th background="#EBF3F8" color="#616161">
                        Action
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody fontSize={"sm"}>
                    {leaves
                      .sort((a, b) => sortByFunc(a, b, sortBy, sortMethodAsc))
                      .map(
                        (
                          { effectiveDate, numLeaves, id, state, stateId },
                          i,
                        ) => (
                          <Tr key={id}>
                            <Td py={"3"}>{i + 1}</Td>
                            <Td py={"3"}>{state}</Td>
                            <Td py={"3"}>{numLeaves}</Td>
                            <Td py={"3"}>{formatDate(effectiveDate)}</Td>
                            <Td py={"2"}>
                              {isFutureDate(new Date(effectiveDate)) &&
                              checkForPermission(
                                PERMISSION.Config["Leaves"].Update,
                              ) ? (
                                <>
                                  <Button
                                    leftIcon={<FiEdit />}
                                    size={"sm"}
                                    variant={"ghost"}
                                    color={"#027DBC"}
                                    onClick={() =>
                                      onEditLeave(
                                        id,
                                        numLeaves,
                                        effectiveDate,
                                        stateId,
                                      )
                                    }
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    ml={"2"}
                                    leftIcon={<AiFillDelete />}
                                    size={"sm"}
                                    variant={"ghost"}
                                    colorScheme="red"
                                    onClick={() =>
                                      onDeleteLeaveConfirmation(
                                        id,
                                        numLeaves,
                                        effectiveDate,
                                        stateId,
                                      )
                                    }
                                  >
                                    Delete
                                  </Button>
                                </>
                              ) : null}
                            </Td>
                          </Tr>
                        ),
                      )}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <>
                <Stack width={"full"}>
                  <Box
                    width={"full"}
                    p={2}
                    rounded="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" color="gray.500">
                      <strong>Note:</strong> Automatically applicable only for
                      female employees in enabled states.
                    </Text>
                  </Box>
                  <TableContainer
                    background="white"
                    width={"full"}
                    border={"1px solid #F2F2F2"}
                    borderRadius={"md"}
                  >
                    <Table variant="simple">
                      <Thead height={"48px"}>
                        <Tr>
                          <Th background="#EBF3F8" color="#616161">
                            Sr. No.
                          </Th>
                          <Th background="#EBF3F8" color="#616161">
                            State
                          </Th>
                          <Th background="#EBF3F8" color="#616161">
                            M-Leave
                          </Th>
                          <Th background="#EBF3F8" color="#616161">
                            Contract Types
                          </Th>
                          <Th background="#EBF3F8" color="#616161">
                            Action
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody fontSize={"sm"}>
                        {statesWithMLeaves
                          .sort((a, b) => {
                            if (a.enabled !== b.enabled) {
                              return a.enabled ? -1 : 1;
                            }

                            return sortByFunc(a, b, "state", true);
                          })
                          .map(
                            ({ id, stateId, enabled, contractTypeIds }, i) => (
                              <Tr key={stateId}>
                                <Td py={"3"}>{i + 1}</Td>
                                <Td py={"3"}>
                                  {
                                    states.find((obj) => obj.id === stateId)
                                      ?.name
                                  }
                                </Td>
                                <Td py={"3"}>
                                  {enabled ? (
                                    <Badge
                                      colorScheme="green"
                                      rounded="full"
                                      px={2}
                                      variant={"solid"}
                                    >
                                      Enabled
                                    </Badge>
                                  ) : (
                                    <Badge
                                      colorScheme="gray"
                                      rounded="full"
                                      px={2}
                                      variant={"outline"}
                                    >
                                      Disabled
                                    </Badge>
                                  )}
                                </Td>
                                <Td py={"3"}>
                                  <Flex gap={"2"} wrap={"wrap"} maxW={"320px"}>
                                    {contractTypeIds.map((id) => {
                                      const contractTypeName =
                                        contractTypes?.find(
                                          (obj) => obj.id === id,
                                        )?.name;
                                      return (
                                        <Badge
                                          key={id}
                                          variant={"subtle"}
                                          background={"#027DBC1a"}
                                          color={enabled ? "#027DBC" : "gray"}
                                          textTransform={"none"}
                                        >
                                          {contractTypeName}
                                        </Badge>
                                      );
                                    })}
                                  </Flex>
                                </Td>

                                <Td py={"2"}>
                                  {checkForPermission(
                                    PERMISSION.Config["Leaves"].Update,
                                  ) ? (
                                    <>
                                      <Button
                                        leftIcon={<FiEdit />}
                                        size={"sm"}
                                        variant={"ghost"}
                                        color={"#027DBC"}
                                        onClick={() =>
                                          onEditMLeave(
                                            id,
                                            enabled,
                                            stateId,
                                            contractTypeIds,
                                          )
                                        }
                                      >
                                        Edit
                                      </Button>
                                    </>
                                  ) : null}
                                </Td>
                              </Tr>
                            ),
                          )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Stack>
              </>
            )}
          </>
        ) : isLoading ? (
          <AppLoader />
        ) : (
          <AppNoData />
        )}
      </Flex>
      <Modal isOpen={isLeaveModalOpen} onClose={onLeaveModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{leaveId ? "Edit" : "Add"} Leaves</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={"4"} isRequired>
              <FormLabel>State</FormLabel>
              <AppSelect
                value={stateId}
                onChange={(value) => setStateId(value)}
                options={
                  states?.length
                    ? states
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(({ id, name }) => ({
                          label: name,
                          value: id,
                        }))
                    : []
                }
              />
            </FormControl>

            <FormControl mb={"4"} isRequired>
              <FormLabel>Effective Year</FormLabel>
              <AppSelect
                value={effectiveDate}
                options={years.map((year) => ({
                  label: year,
                  value: moment([year]).format("YYYY-MM-DD"),
                }))}
                onChange={(date) => setEffectiveDate(date)}
              />
              {/* <SingleDatepicker
                date={effectiveDate ? new Date(effectiveDate) : undefined}
                onDateChange={(date) =>
                  setEffectiveDate(moment(date).format("YYYY-MM-DD"))
                }
                minDate={subDays(new Date(), 1)}
                configs={{
                  dateFormat: "dd-MM-yyyy",
                }}
              /> */}
            </FormControl>
            <FormControl mb={"4"} isRequired>
              <FormLabel>Number of Leaves</FormLabel>
              <Input
                placeholder="Enter here"
                type="number"
                value={numLeaves}
                onChange={(e) => setNumLeaves(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              fontSize={"sm"}
              mr={3}
              onClick={onLeaveModalClose}
            >
              Close
            </Button>
            <Button
              isDisabled={!stateId || !effectiveDate || !numLeaves}
              onClick={onSaveLeaves}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isMLeaveModalOpen} onClose={onMLeaveModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{"Edit"} M-Leave Policy</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={"4"} isRequired>
              <FormLabel>State</FormLabel>
              <AppSelect
                value={stateId}
                onChange={(value) => setStateId(value)}
                disabled
                options={
                  states?.length
                    ? states
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(({ id, name }) => ({
                          label: name,
                          value: id,
                        }))
                    : []
                }
              />
            </FormControl>

            <FormControl mb={"4"} isRequired>
              <FormLabel>M-Leave Status</FormLabel>
              <RadioGroup
                value={enabled ? "enabled" : "disabled"}
                onChange={(value) => {
                  setErr("");
                  setEnabled(value === "enabled");
                }}
                mb={"2"}
              >
                <Stack direction="row">
                  {["enabled", "disabled"].map((value) => (
                    <Radio value={value} key={value}>
                      <Text
                        style={{
                          textTransform: "capitalize",
                        }}
                      >
                        {value}
                      </Text>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
            {enabled ? (
              <FormControl mb={"4"} isRequired>
                <FormLabel>Contract Types</FormLabel>
                <AppSelect
                  isMulti
                  onChange={(value) => {
                    setErr("");
                    setContractTypeIds(value);
                  }}
                  value={contractTypeIds}
                  options={
                    contractTypes?.length
                      ? contractTypes
                          .filter(({ id, name }) => id && name)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(({ id, name }) => ({
                            label: name,
                            value: id,
                          }))
                      : []
                  }
                />
              </FormControl>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Flex width={"full"} direction={"column"}>
              {err ? (
                <Text
                  width={"full"}
                  background={"#fff7d6"}
                  color={"#907400"}
                  fontSize={"xs"}
                  px={"4"}
                  py={"2"}
                  rounded={"md"}
                  textAlign={"center"}
                  mb={"4"}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: err,
                    }}
                  ></span>
                </Text>
              ) : null}

              <Flex justifyContent={"flex-end"}>
                <Button
                  variant="outline"
                  fontSize={"sm"}
                  mr={3}
                  onClick={onMLeaveModalClose}
                >
                  Close
                </Button>
                <Button
                  onClick={onSaveMLeaves}
                  isDisabled={enabled ? !contractTypeIds.length : false}
                >
                  Save
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Leaves</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to delete?</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              fontSize={"sm"}
              mr={3}
              onClick={onDeleteModalClose}
            >
              Close
            </Button>
            <Button
              colorScheme="red"
              variant={"solid"}
              fontSize={"sm"}
              isDisabled={!stateId || !effectiveDate || !numLeaves}
              onClick={onDeleteLeaves}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AppContainer>
  );
}

export default MangeLeaves;
